/* eslint-disable react/no-array-index-key, consistent-return, @typescript-eslint/no-shadow, prettier/prettier, @typescript-eslint/no-unused-vars */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useStyletron } from 'baseui';
import { Select } from 'baseui/select';
import { RadioGroup, Radio, ALIGN } from 'baseui/radio';
import { Button, KIND, SIZE } from 'baseui/button';
import { Spinner } from 'baseui/spinner';
import { ENVIRONMENTS } from '../../utils/constants';
import useAnalyze from './useAnalyze';
import ValueEditor from './ValueEditor';
import ImpactReport from './ImpactReport';

const ConfigAnalyze: React.FC<{}> = () => {
	const [css] = useStyletron();
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const initialKey = params.get('key') || '';

	const {
		keyName,
		setKeyName,
		keyDetail,
		currentValue,
		setCurrentValue,
		proposedValue,
		setProposedValue,
		environment,
		setEnvironment,
		selectedService,
		setSelectedService,
		serviceOptions,
		isLoading,
		isLoadingDetail,
		report,
		handleAnalyze,
		configKeyOptions,
		loadConfigKeys
	} = useAnalyze(initialKey);

	useEffect(() => {
		if (initialKey) {
			setKeyName(initialKey);
		}
	}, [initialKey, setKeyName]);

	return (
		<div className={css({ padding: '24px', maxWidth: '1200px' })}>
			<h2 className={css({ marginTop: '0', marginBottom: '24px' })}>Impact Analysis</h2>

			{/* Key Selection */}
			<div
				className={css({
					display: 'grid',
					gridTemplateColumns: '2fr 1fr 1fr',
					gap: '16px',
					marginBottom: '24px'
				})}>
				<div>
					<label className={css({ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' })}>
						Config Key
					</label>
					<Select
						options={configKeyOptions}
						value={keyName ? [{ label: keyName, id: keyName }] : []}
						placeholder='Search for a config key...'
						onChange={(params) => {
							const selected = params.value?.[0];
							setKeyName((selected?.id as string) || '');
						}}
						onInputChange={(event) => {
							const value = (event?.target as HTMLInputElement)?.value;
							if (value !== undefined) loadConfigKeys(value);
						}}
						type='search'
					/>
				</div>
				<div>
					<label className={css({ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' })}>
						Service
					</label>
					<Select
						options={serviceOptions}
						value={selectedService ? [{ label: selectedService, id: selectedService }] : []}
						placeholder={serviceOptions.length ? 'Select service...' : 'Pick a key first'}
						disabled={!keyName || serviceOptions.length === 0}
						clearable
						onChange={(params) => {
							const selected = params.value?.[0];
							setSelectedService((selected?.id as string) || '');
						}}
					/>
				</div>
				<div>
					<label className={css({ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' })}>
						Environment
					</label>
					<RadioGroup value={environment} onChange={(e) => setEnvironment(e.currentTarget.value)} align={ALIGN.horizontal}>
						{ENVIRONMENTS.map((env) => (
							<Radio key={env.id} value={env.id}>
								{env.label}
							</Radio>
						))}
					</RadioGroup>
				</div>
			</div>

			{/* Key Info */}
			{isLoadingDetail && (
				<div className={css({ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' })}>
					<Spinner $size={20} />
					<span className={css({ fontSize: '14px', color: '#666' })}>Loading key details...</span>
				</div>
			)}

			{keyDetail && !isLoadingDetail && (
				<div
					className={css({
						backgroundColor: '#F6F6F6',
						padding: '12px 16px',
						borderRadius: '8px',
						marginBottom: '16px',
						fontSize: '13px',
						display: 'flex',
						gap: '24px'
					})}>
					<span>
						Type: <strong>{keyDetail.dataType}</strong>
					</span>
					<span>
						Category: <strong>{keyDetail.businessCategory}</strong>
					</span>
					<span>
						Risk: <strong>{keyDetail.riskLevel}</strong>
					</span>
					<span>
						Services: <strong>{keyDetail.serviceCount}</strong>
					</span>
				</div>
			)}

			{/* Value Editor */}
			{keyName && (
				<div className={css({ marginBottom: '24px' })}>
					<ValueEditor
						currentValue={currentValue}
						proposedValue={proposedValue}
						onProposedChange={setProposedValue}
						onCurrentChange={setCurrentValue}
					/>
				</div>
			)}

			{/* Analyze Button */}
			<Button
				kind={KIND.primary}
				size={SIZE.default}
				onClick={handleAnalyze}
				disabled={!keyName || !proposedValue || isLoading}
				isLoading={isLoading}>
				Analyze Impact
			</Button>

			{/* Report */}
			{report && <ImpactReport report={report} />}
		</div>
	);
};

export default ConfigAnalyze;
